import React, { useState, useRef, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Button,
  Pagination,
  Paper,
  Skeleton,
  Tooltip,
  InputAdornment
} from "@mui/material";
import { AndroidIcon, AppleAppIcon, ExportIcon, ManageColumnIcon, WebIcon } from "../../icons";
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import SmartphoneOutlinedIcon from '@mui/icons-material/SmartphoneOutlined';
import PaymentTimelineModal from "./PaymentTimelineModal";
import { fetchPaymentAnalyticsList } from "../../api/paymentAnalytics";
import Modal from "@mui/material/Modal";
import UserProfileMainPage from "../user-profile-main/UserProfileMainPage";
import moment from "moment";
import { statusLabel } from "../../utils/memberStatus";
import { useDispatch, useSelector } from "react-redux";
import { updateMultipleColumns, resetColumns } from "../../containers/app-container/reducer/visibleColumnsSlice";
import { cellSx, headSx, stickySx, stickyHeadSx, outlineBtnSx, paginationSx, STATUS_STYLE_MAP, DEFAULT_STATUS_STYLE, Muted, CountPill } from "../common/table/tableKit";
import { displayPaymentReason } from "../../utils/paymentReason";
import ColumnsDrawer from "../common/table/ColumnsDrawer";

const profileModalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 1200,
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  boxShadow: 24,
  borderRadius: '12px',
  overflowY: 'auto',
  p: 0,
};

// Server sends { label, icon } for both the member's device and the store/gateway they pay through.
const PLATFORM_ICON = { ios: AppleAppIcon, android: AndroidIcon, web: WebIcon };

const COUNT_TONE = { successfulPayment: "#008E40", cancelledPayment: "#C77700", failedPayment: "#D93025" };

const normalizePaymentRow = (row, index, page, rowsPerPage) => {
  // An older server sends only its "Active"/"Inactive" text; show it as-is until it updates.
  const bucket = row.statusBucket || null;
  const status = bucket ? statusLabel(bucket) : (row.profileStatus || row.status || "-");
  return {
    ...row,
    id: (page - 1) * rowsPerPage + index + 1,
    _id: row._id || row.userId || "",
    userId: row.userId || row._id || "",
    firstName: row.firstName || row.userName || "",
    email: row.email || "",
    gender: row.gender || "",
    city: row.city || row.location?.city || "",
    country: row.country || row.location?.country || "",
    profileStatus: status,
    statusStyle: STATUS_STYLE_MAP[bucket] || DEFAULT_STATUS_STYLE,
    paywallViewed: row.paywallViewed ?? 0,
    purchaseStarted: row.purchaseStarted ?? 0,
    successfulPayment: row.successfulPayment ?? 0,
    cancelledPayment: row.cancelledPayment ?? 0,
    failedPayment: row.failedPayment ?? 0,
    reason: displayPaymentReason(row.reason) || "",
    // Per-event cards (Purchase Started / Payment Success / ...) carry the event time as
    // `eventTimestamp`; prefer it so "Payment Failed At" is the event, not user.updatedAt.
    lastActivityTimestamp: row.lastActivityTimestamp || row.eventTimestamp || row.lastActivity || row.updatedAt || "",
  };
};

// The list endpoint only sorts by activity time (asc/desc), so that is the one sortable column.
const columns = [
  { key: "id", label: "#" },
  { key: "member", label: "Member", required: true },
  { key: "location", label: "Location" },
  { key: "profileStatus", label: "Status", align: "center" },
  { key: "userPlatform", label: "Platform" },
  { key: "paymentVia", label: "Paid Via" },
  { key: "planTried", label: "Plan" },
  { key: "paywallViewed", label: "Paywall", align: "center" },
  { key: "purchaseStarted", label: "Started", align: "center" },
  { key: "successfulPayment", label: "Success", align: "center" },
  { key: "cancelledPayment", label: "Cancelled", align: "center" },
  { key: "failedPayment", label: "Failed", align: "center" },
  { key: "reason", label: "Reason" },
  { key: "lastActivityTimestamp", label: "Last Activity", sortable: true },
  { key: "actions", label: "Actions", align: "center" },
];

const allColumnsOn = () => columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {});

// Per-event cards return literal 0 for every funnel count, so those columns only show on the totals views.
const defaultColumnsFor = (activeCard) => {
  if (!activeCard || activeCard === "totalPaywall" || activeCard === "paywallViewed") return allColumnsOn();
  const on = ["id", "member", "location", "profileStatus", "userPlatform", "paymentVia", "planTried", "lastActivityTimestamp", "actions"];
  // Both carry a reason — USER_CANCELLED for a dismissed sheet, the Play/verify cause for a failure.
  if (activeCard === "failedPayment" || activeCard === "cancelledPayment") on.push("reason");
  return columns.reduce((acc, col) => ({ ...acc, [col.key]: on.includes(col.key) }), {});
};

const ACTIVITY_LABEL = {
  purchaseStarted: "Purchase Started At",
  successfulPayment: "Payment Success At",
  cancelledPayment: "Payment Cancelled At",
  failedPayment: "Payment Failed At",
};

const HEADING = {
  purchaseStarted: ["Purchase Started", "Members who started a checkout in the selected period."],
  successfulPayment: ["Successful Payments", "Every completed payment, with the store or gateway it went through."],
  cancelledPayment: ["Cancelled Payments", "Checkouts the member dismissed before paying."],
  failedPayment: ["Failed Payments", "Payments that errored, with the reason the store or gateway gave."],
  uniqueUsers: ["Unique Users", "Members with payment activity in the selected period."],
};
const DEFAULT_HEADING = ["Payment Analytics Details", "Each member's path through the paywall, the device they use and how they pay."];

const PlatformBadge = ({ value }) => {
  if (!value?.label) return <Muted />;
  const Icon = PLATFORM_ICON[value.icon];
  return (
    <Box display="inline-flex" alignItems="center" gap={0.75}
      sx={{ px: 1, py: "3px", border: "1px solid #EAEAEA", borderRadius: "6px", bgcolor: "#FAFAFA", whiteSpace: "nowrap", fontSize: 13, color: "#3C4043" }}>
      {Icon ? <Icon width={14} height={14} /> : <SmartphoneOutlinedIcon sx={{ fontSize: 15, color: "#5F6368" }} />}
      {value.label}
    </Box>
  );
};

const PaymentAnalyticsDetailsTable = ({ filter = {}, activeCard }) => {
  const debounceRef = useRef(null);
  const [openHistory, setOpenHistory] = useState(false)
  const [openProfile, setOpenProfile] = useState(false);
  const [activeUser, setActiveUser] = useState(null);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const rowsPerPage = 10;

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'desc' });

  const { action, funnelType } = useMemo(() => {
    const mapping = {
      paywallViewed: { action: 'VIEW_PAYMENTWALL', funnelType: 'MONETIZATION' },
      purchaseStarted: { action: 'START_PAYMENT', funnelType: 'MONETIZATION' },
      successfulPayment: { action: 'PAYMENT_SUCCESS', funnelType: 'MONETIZATION' },
      cancelledPayment: { action: 'PAYMENT_CANCELLED', funnelType: 'MONETIZATION' },
      failedPayment: { action: 'PAYMENT_FAILED', funnelType: 'MONETIZATION' },
      uniqueUsers: { action: 'OPEN_APP', funnelType: 'ENGAGEMENT' },
      totalPaywall: { action: 'VIEW_PAYMENTWALL', funnelType: 'MONETIZATION' },
    };
    return mapping[activeCard] || { action: undefined, funnelType: undefined };
  }, [activeCard]);

  // Debounce search input -> committed search term
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(searchInput);
      setPage(1);
    }, 400);
    return () => clearTimeout(debounceRef.current);
  }, [searchInput]);

  // Reset to first page when upstream filter changes
  useEffect(() => {
    setPage(1);
  }, [JSON.stringify(filter)]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await fetchPaymentAnalyticsList({
        filter,
        setData: (apiRows) => {
          setRows((apiRows || []).map((row, index) => normalizePaymentRow(row, index, page, rowsPerPage)));
        },
        page,
        limit: rowsPerPage,
        searchTerm: search || undefined,
        sortBy: sortConfig.key || undefined,
        sortOrder: sortConfig.direction || undefined,
        action,
        funnelType,
      });
      if (data) {
        const total = data.totalRecords ?? 0;
        setTotalRecords(total);
        setTotalPages(data.totalPages ?? Math.max(1, Math.ceil(total / rowsPerPage)));
      } else {
        setTotalRecords(0);
        setTotalPages(0);
      }
    } catch (err) {
      console.error("Error while loading payment analytics list", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [JSON.stringify(filter), page, search, sortConfig.key, sortConfig.direction, action, funnelType]);

  // The admin's column choice is kept per card view in the persisted visibleColumns store.
  const dispatch = useDispatch();
  const columnsKey = `paymentAnalytics:${activeCard || "all"}`;
  const savedColumns = useSelector((state) => state.visibleColumns.pages[columnsKey]);
  const visibleColumns = useMemo(() => ({ ...defaultColumnsFor(activeCard), ...savedColumns }), [activeCard, savedColumns]);
  const [openColumnsDrawer, setOpenColumnsDrawer] = useState(false);

  const requestSort = (key) => {
    if (!columns.find(col => col.key === key)?.sortable) return;
    const direction = sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc';
    setSortConfig({ key, direction });
    setPage(1);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
  };

  const [heading, subheading] = HEADING[activeCard] || DEFAULT_HEADING;
  const shownColumns = columns.filter(col => visibleColumns[col.key]);
  const firstShown = (page - 1) * rowsPerPage + 1;

  const renderCell = (col, row) => {
    switch (col.key) {
      case "id":
        return <Box component="td" key={col.key} sx={{ ...cellSx, color: "#9AA0A6", width: 40 }}>{row.id}</Box>;
      case "member":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, ...stickySx, minWidth: 200 }}>
            <Typography
              onClick={() => { if (row.userId) { setActiveUser(row); setOpenProfile(true); } }}
              sx={{ fontSize: 14, fontWeight: 600, color: "#1F1F1F", textTransform: "capitalize", cursor: row.userId ? "pointer" : "default", "&:hover": { color: "#2E80DB" } }}
            >
              {row.firstName || "Unnamed"}
              {row.gender && <Box component="span" sx={{ ml: 0.75, fontSize: 12, fontWeight: 400, color: "#9AA0A6" }}>· {row.gender}</Box>}
            </Typography>
            <Typography sx={{ fontSize: 12, color: "#6A7282", wordBreak: "break-all" }}>{row.email || "—"}</Typography>
          </Box>
        );
      case "location":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, minWidth: 120 }}>
            {row.city || row.country ? (
              <>
                <Typography sx={{ fontSize: 13, color: "#3C4043" }}>{row.city || row.country}</Typography>
                {row.city && <Typography sx={{ fontSize: 12, color: "#9AA0A6" }}>{row.country}</Typography>}
              </>
            ) : <Muted />}
          </Box>
        );
      case "profileStatus":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, textAlign: "center" }}>
            <Box sx={{
              backgroundColor: row.statusStyle.bg, color: row.statusStyle.color, fontSize: 12, fontWeight: 600,
              px: 1.25, py: "3px", borderRadius: "12px", display: "inline-block", whiteSpace: "nowrap"
            }}>
              {row.profileStatus}
            </Box>
          </Box>
        );
      case "userPlatform":
      case "paymentVia":
        return <Box component="td" key={col.key} sx={cellSx}><PlatformBadge value={row[col.key]} /></Box>;
      case "planTried":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, whiteSpace: "nowrap" }}>
            {row.planTried ? (
              <Box component="span" sx={{
                px: 1, py: "3px", borderRadius: "6px", fontSize: 12, fontWeight: 600,
                ...(/^luxury/i.test(row.planTried) ? { color: "#7A4E00", bgcolor: "#F6EDD9" } : { color: "#8A6D00", bgcolor: "#FFF6D6" })
              }}>
                {row.planTried}
              </Box>
            ) : <Muted />}
          </Box>
        );
      case "paywallViewed":
      case "purchaseStarted":
      case "successfulPayment":
      case "cancelledPayment":
      case "failedPayment":
        return <Box component="td" key={col.key} sx={{ ...cellSx, textAlign: "center" }}><CountPill value={row[col.key]} tone={COUNT_TONE[col.key]} /></Box>;
      case "reason":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, maxWidth: 220 }}>
            {row.reason ? (
              <Tooltip title={row.reason} placement="top-start">
                <Typography sx={{ fontSize: 12, color: "#B3261E", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {row.reason}
                </Typography>
              </Tooltip>
            ) : <Muted />}
          </Box>
        );
      case "lastActivityTimestamp":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, whiteSpace: "nowrap" }}>
            {row.lastActivityTimestamp ? (
              <Tooltip title={moment(row.lastActivityTimestamp).fromNow()} placement="top-start">
                <Box>
                  <Typography sx={{ fontSize: 13, color: "#3C4043" }}>{moment(row.lastActivityTimestamp).format("DD MMM YYYY")}</Typography>
                  <Typography sx={{ fontSize: 12, color: "#9AA0A6" }}>{moment(row.lastActivityTimestamp).format("hh:mm A")}</Typography>
                </Box>
              </Tooltip>
            ) : <Muted />}
          </Box>
        );
      case "actions":
        return (
          <Box component="td" key={col.key} sx={{ ...cellSx, textAlign: "center", whiteSpace: "nowrap" }}>
            <Tooltip title="Payment timeline">
              <span>
                <IconButton size="small" disabled={!row.userId} onClick={() => { setActiveUser(row); setOpenHistory(true); }}
                  sx={{ border: "1px solid #E6E6E6", borderRadius: "8px", mr: 0.75, color: "#5F6368", "&:hover": { color: "#BE1F1F", borderColor: "#BE1F1F" } }}>
                  <TimelineOutlinedIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="View profile">
              <span>
                <IconButton size="small" disabled={!row.userId} onClick={() => { setActiveUser(row); setOpenProfile(true); }}
                  sx={{ border: "1px solid #E6E6E6", borderRadius: "8px", color: "#5F6368", "&:hover": { color: "#BE1F1F", borderColor: "#BE1F1F" } }}>
                  <PersonOutlineIcon sx={{ fontSize: 18 }} />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        );
      default:
        return <Box component="td" key={col.key} sx={cellSx}>{row[col.key]}</Box>;
    }
  };

  return (
    <Box p={0} sx={{ pt: 3 }}>
      {/* HEADER */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap" gap={2} mb={2}>
        <Box>
          <Typography fontWeight={600} sx={{ fontSize: '20px', color: "#000" }}>{heading}</Typography>
          <Typography sx={{ fontSize: '14px', color: '#6A7282', pt: 0.5 }}>{subheading}</Typography>
        </Box>
        <Box display="flex" gap={1.5}>
          <TextField
            size="small"
            placeholder="Search name, email, city"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: "#9AA0A6" }} /></InputAdornment>,
              endAdornment: searchInput ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}><CloseIcon sx={{ fontSize: 16 }} /></IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: 260,
              '& .MuiOutlinedInput-root': {
                height: 36,
                fontSize: '13px',
                borderRadius: '8px',
                '& fieldset': { borderColor: 'rgb(0, 0, 0, 0.1)' },
                '&:hover fieldset': { borderColor: '#BE1F1F' },
                '&.Mui-focused fieldset': { borderColor: '#BE1F1F', borderWidth: 1.5 }
              },
            }}
          />
          <Button onClick={() => setOpenColumnsDrawer(true)} sx={outlineBtnSx}>
            <ManageColumnIcon /> Columns
          </Button>
          <Button variant="outlined" sx={outlineBtnSx}>
            <ExportIcon /> Export
          </Button>
        </Box>
      </Box>

      {/* TABLE */}
      <Paper sx={{ boxShadow: '0 1px 2px rgba(16,24,40,0.04)', border: '1px solid #ECECEC', borderRadius: '12px', overflow: 'hidden' }}>
        <Box sx={{
          overflow: "auto",
          maxHeight: "calc(100vh - 250px)",
          "&::-webkit-scrollbar": { height: 8, width: 8 },
          "&::-webkit-scrollbar-thumb": { backgroundColor: "#D0D0D0", borderRadius: 4 },
        }}>
          <table style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
            <thead>
              <tr>
                {shownColumns.map((col) => (
                  <Box
                    component="th"
                    key={col.key}
                    onClick={() => requestSort(col.key)}
                    sx={{ ...headSx, ...(col.key === "member" && stickyHeadSx), textAlign: col.align || "left", cursor: col.sortable ? "pointer" : "default", ...(col.sortable && { "&:hover": { color: "#1F1F1F" } }) }}
                  >
                    <Box display="inline-flex" alignItems="center">
                      {col.key === "lastActivityTimestamp" ? (ACTIVITY_LABEL[activeCard] || col.label) : col.label}
                      {col.sortable && (sortConfig.key === col.key && sortConfig.direction === "asc"
                        ? <ArrowUpwardIcon sx={{ fontSize: 14, ml: 0.5, color: sortConfig.key === col.key ? '#BE1F1F' : '#B0B0B0' }} />
                        : <ArrowDownwardIcon sx={{ fontSize: 14, ml: 0.5, color: sortConfig.key === col.key ? '#BE1F1F' : '#B0B0B0' }} />)}
                    </Box>
                  </Box>
                ))}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                Array.from({ length: rowsPerPage }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {shownColumns.map((col) => (
                      <Box component="td" key={col.key} sx={cellSx}>
                        <Skeleton variant="text" height={20} width={col.key === "member" ? 140 : "70%"} />
                      </Box>
                    ))}
                  </tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={shownColumns.length} style={{ padding: "56px 12px", textAlign: "center" }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
                      <SearchOffIcon sx={{ fontSize: 44, color: '#BDBDBD' }} />
                      <Typography sx={{ fontSize: 16, color: '#424242', fontWeight: 600 }}>No payment activity found</Typography>
                      <Typography sx={{ fontSize: 13, color: '#757575' }}>
                        {search ? `Nothing matches "${search}".` : 'No payment records match the current filters.'}
                      </Typography>
                      {search && (
                        <Button variant="outlined" size="small" onClick={clearSearch}
                          sx={{ borderColor: '#BE1F1F', color: '#BE1F1F', textTransform: 'none', '&:hover': { borderColor: '#BE1F1F', backgroundColor: 'rgba(190,31,31,0.04)' } }}>
                          Clear search
                        </Button>
                      )}
                    </Box>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <Box component="tr" key={row.eventId || `${row.userId}-${row.id}`} sx={{ "&:hover td": { backgroundColor: "#FCFAF7" } }}>
                    {shownColumns.map((col) => renderCell(col, row))}
                  </Box>
                ))
              )}
            </tbody>
          </table>
        </Box>

        {/* FOOTER */}
        <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1.5}
          sx={{ px: 2, py: 1.5, borderTop: "1px solid #F0F0F0", bgcolor: "#FCFCFC" }}>
          <Typography sx={{ fontSize: 13, color: '#6A7282' }}>
            {loading ? "Loading…" : totalRecords === 0 ? "0 records" : (
              <>Showing <strong>{firstShown}–{firstShown + rows.length - 1}</strong> of <strong>{totalRecords.toLocaleString()}</strong></>
            )}
          </Typography>
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              size="small"
              shape="rounded"
              onChange={(e, v) => setPage(v)}
              sx={paginationSx}
            />
          )}
        </Box>
      </Paper>

      <ColumnsDrawer
        open={openColumnsDrawer}
        onClose={() => setOpenColumnsDrawer(false)}
        columns={columns}
        visible={visibleColumns}
        onApply={(updates) => dispatch(updateMultipleColumns({ pageKey: columnsKey, updates }))}
        onReset={() => dispatch(resetColumns({ pageKey: columnsKey }))}
      />

      <PaymentTimelineModal
        open={openHistory}
        onClose={() => setOpenHistory(false)}
        userId={activeUser?.userId || activeUser?._id}
        userEmail={activeUser?.email}
      />

      <Modal open={openProfile} onClose={() => setOpenProfile(false)} aria-labelledby="payment-profile-modal">
        <Box sx={profileModalStyle}>
          <Box sx={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 10, padding: '10px', display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
            <IconButton onClick={() => setOpenProfile(false)}>
              <CloseIcon />
            </IconButton>
          </Box>
          {(activeUser?.userId || activeUser?._id) && (
            <UserProfileMainPage userId={activeUser.userId || activeUser._id} />
          )}
        </Box>
      </Modal>
    </Box>
  );
};

export default PaymentAnalyticsDetailsTable;
